import { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { ColumnContentType } from 'components/DataTable/columnSizing'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { cn } from 'components/UI/cn'
import {
  formatDate,
  integerFormatterOrFallback,
  usdFormatterOrFallback,
} from 'utils/formatting'
import { FrameDef, frameOf } from '../frames'
import { formatMeasure } from '../format'
import { goalMet } from '../kpis'
import { FrameRow, GroupRow, QueryResult, ViewConfig } from '../view-config'

/**
 * Table for either result kind:
 * - aggregate: one row per group + a TOTAL footer row (SQL rollup analog)
 * - list: one row per deal, columns from config.columns
 *
 * The row-cap switcher lives in the page's filter toolbar (see RowLimitControl),
 * not here — this component is purely the table.
 *
 * Column sizing/alignment follows the DS2026 table model (see
 * components/DataTable/columnSizing): every column declares a `columnType` so
 * the table can enforce content-type min-widths, and numeric columns right-align
 * (header + values) — the conventional treatment that also stops a trailing
 * metric column from reading as "centered". Data columns are NEVER flagged
 * `isLastColumn`, so the actions-column centering rule never applies to them.
 */

/** Max split-value columns a pivot shows before capping (excess noted, not dropped silently). */
const PIVOT_MAX_COLS = 12

/** The frame's declared type for a column (cat/num/date), if known. */
const fieldType = (
  frame: FrameDef,
  field: string
): 'cat' | 'num' | 'date' | undefined =>
  frame.fields.find((f) => f.name === field)?.type

/** A revenue/currency measure reads as USD; other numbers as plain integers. */
const isCurrency = (field: string): boolean => /revenue/i.test(field)

/** A field's content type drives its min-width floor and alignment. */
const columnTypeForField = (
  field: string,
  frame: FrameDef
): ColumnContentType => {
  if (field === 'company_name') return 'company-name'
  const t = fieldType(frame, field)
  if (t === 'date') return 'date'
  if (t === 'num') return isCurrency(field) ? 'currency' : 'number'
  return 'text'
}

/** Column display label for a frame (falls back to the raw field name). */
export const fieldLabelOf = (frame: FrameDef, field: string): string =>
  frame.labels[field] ?? field

/**
 * Header for an aggregate table's leading (group-key) column: a time bucket reads
 * as Quarter/Month, a categorical group as its field label, a rollup-only metric
 * report has none. Shared by the plain summary table and the pivot.
 */
const groupColumnHeader = (config: ViewConfig, frame: FrameDef): string => {
  if (!config.groupBy) return ''
  if (config.groupBy.bucket === 'quarter') return 'Quarter'
  if (config.groupBy.bucket === 'month') return 'Month'
  return fieldLabelOf(frame, config.groupBy.field)
}

/** A metric column reads as currency when its measure is a revenue, else a count. */
const metricColumnType = (label: string): ColumnContentType =>
  label.includes('Revenue') ? 'currency' : 'count'

export const formatCell = (
  field: string,
  value: unknown,
  frame: FrameDef
): string => {
  const t = fieldType(frame, field)
  if (t === 'num')
    return isCurrency(field)
      ? usdFormatterOrFallback(value as number | null)
      : integerFormatterOrFallback(value as number | null)
  if (t === 'date') return value == null ? '-' : formatDate(value as string)
  if (Array.isArray(value)) return value.length === 0 ? '-' : value.join(', ')
  return value == null ? '-' : String(value)
}

/** Summary-table metric cells share the renderer-wide measure formatter. */
const formatMetric = formatMeasure

/**
 * Render a graded metric cell: the formatted value, coloured pass/fail when the
 * row is being graded against a goal, plain otherwise. Only the report's
 * *targeted* measure (chartMetric) is graded, and only for grouped rows — this
 * is what turns a single goal into a per-row KPI (the chart draws the goal line;
 * the table colours each row's value to match). Pass/fail rule: `goalMet`.
 */
const gradedMetricCell = (
  label: string,
  value: number,
  graded: boolean,
  threshold: ViewConfig['threshold']
) => {
  const text = formatMetric(label, value)
  if (!graded) return text
  const pass = goalMet(value, threshold)
  if (pass === undefined) return text
  return (
    <span
      className={cn(
        'font-semibold tabular-nums',
        pass ? 'text-success' : 'text-error'
      )}
    >
      {text}
    </span>
  )
}

export function ReportTable({
  config,
  result,
  fill = false,
  sortable = false,
}: {
  config: ViewConfig
  result: QueryResult
  /** Enable header-click sorting on the row-level (list) table. */
  sortable?: boolean
  /**
   * Stretch a row-level (list) table to its container's full height (the table
   * scrolls internally via ClientDataTable). The card/page must supply that
   * height (see TableCard `fill`). Aggregate tables ignore this — they stay
   * `height="auto"` since they're only a handful of rows.
   */
  fill?: boolean
}) {
  const frame = frameOf(config.frame)
  if (result.kind === 'aggregate') {
    // Pivot: a group-by with a split (seriesBy) reads as a cross-tab — group on
    // rows, each split value a column, the chosen measure in the cells, plus a
    // Total column (per row) and Total row (the engine's per-series rollup). The
    // chart views show the same data as bars/lines; this is its Excel form.
    if (config.groupBy && config.seriesBy && result.seriesKeys?.length) {
      const metricLabel = config.chartMetric ?? config.metrics![0].label
      const colType = metricColumnType(metricLabel)
      // Columns ordered by grand total (desc) and capped — a wide split (e.g.
      // every product) would otherwise sprawl. Dropped columns are surfaced in a
      // note, never silently hidden, and the Total column still reflects all.
      const ranked = [...result.seriesKeys].sort(
        (a, b) =>
          (result.total.series?.[b]?.[metricLabel] ?? 0) -
          (result.total.series?.[a]?.[metricLabel] ?? 0)
      )
      const shownKeys = ranked.slice(0, PIVOT_MAX_COLS)
      const hidden = ranked.length - shownKeys.length
      const columns: ColumnDef<GroupRow, any>[] = [
        {
          id: 'key',
          header: groupColumnHeader(config, frame),
          accessorFn: (g) => (g.key === 'TOTAL' ? 'Total' : g.key),
          meta: { columnType: 'text', isFirstColumn: true },
        },
        ...shownKeys.map(
          (sk): ColumnDef<GroupRow, any> => ({
            id: sk,
            header: sk,
            accessorFn: (g) => g.series?.[sk]?.[metricLabel],
            cell: (ctx) => formatMetric(metricLabel, ctx.getValue() as number),
            meta: { columnType: colType },
          })
        ),
        {
          id: '__total__',
          header: 'Total',
          accessorFn: (g) => g.metrics[metricLabel],
          cell: (ctx) => formatMetric(metricLabel, ctx.getValue() as number),
          meta: { columnType: colType },
        },
      ]
      return (
        <div className="reporting-table contents">
          <TableLinkStyleProvider variant="plain">
            <ClientDataTable
              columns={columns}
              data={[...result.groups, result.total]}
              enableSorting={false}
              height="auto"
            />
          </TableLinkStyleProvider>
          {hidden > 0 && (
            <p className="px-1 pt-2 text-xs text-fg-secondary">
              Showing top {PIVOT_MAX_COLS} of {ranked.length}{' '}
              {fieldLabelOf(frame, config.seriesBy)} columns by {metricLabel}.
            </p>
          )}
        </div>
      )
    }

    const metricLabels = config.metrics!.map((m) => m.label)
    // The targeted measure — the one the threshold/KPI grades each row against
    // (same measure the chart plots). Only grade grouped rows: a metric report's
    // single row is the rollup, shown with its own met/missed badge in MetricView.
    const targetLabel = config.chartMetric ?? metricLabels[0]
    const columns: ColumnDef<GroupRow, any>[] = [
      {
        id: 'key',
        // A metric report has no groupBy — its single row is the rollup total.
        header: groupColumnHeader(config, frame),
        accessorFn: (g) => (g.key === 'TOTAL' ? 'Total' : g.key),
        meta: { columnType: 'text', isFirstColumn: true },
      },
      ...metricLabels.map(
        (label): ColumnDef<GroupRow, any> => ({
          id: label,
          header: label,
          accessorFn: (g) => g.metrics[label],
          cell: (ctx) =>
            gradedMetricCell(
              label,
              ctx.getValue() as number,
              !!config.groupBy && label === targetLabel,
              config.threshold
            ),
          // Reporting tables left-align everything (header + cells); columnType
          // still drives min-width floors, but no right-align/centering.
          meta: { columnType: metricColumnType(label) },
        })
      ),
    ]
    // A grouped table shows its rows only — no Total footer (the pivot above
    // keeps one, since its Total row/column is the whole point). A metric report
    // has no groupBy: its single row IS the rollup total, so render that.
    const rows = config.groupBy ? result.groups : [result.total]
    return (
      <div className="reporting-table contents">
        <TableLinkStyleProvider variant="plain">
          <ClientDataTable
            columns={columns}
            data={rows}
            enableSorting={false}
            height="auto"
          />
        </TableLinkStyleProvider>
      </div>
    )
  }

  // `columns: undefined` means "all columns" — a raw frame shows everything
  // until a Columns operation narrows it.
  const shownColumns = config.columns ?? frame.columns
  const columns: ColumnDef<FrameRow, any>[] = shownColumns.map((field, i) => ({
    id: field,
    header: fieldLabelOf(frame, field),
    accessorFn: (d: FrameRow) => d[field],
    cell: (ctx) => formatCell(field, ctx.getValue(), frame),
    // Reporting tables left-align every column (header + cells); columnType
    // still sets the min-width floor for resizable columns.
    meta: {
      columnType: columnTypeForField(field, frame),
      isFirstColumn: i === 0,
    },
  }))

  return (
    <div className="reporting-table contents">
      <TableLinkStyleProvider variant="plain">
        {/* `fill` → height 100% so the table grows to the page's remaining
          height (TableCard supplies the constrained flex height) and scrolls
          internally. Otherwise no `height` → ClientDataTable's default fixed
          height with internal virtualized scroll. Either way long row lists
          scroll rather than growing the page. Row-level tables also opt into
          DS2026 drag-resizable columns with content-type min-widths + per-user
          persisted widths (tableKey namespaces the saved widths). */}
        <ClientDataTable
          columns={columns}
          data={result.rows}
          enableSorting={sortable}
          enableColumnResizing
          tableKey={`reporting-${config.id}`}
          height={fill ? '100%' : undefined}
        />
      </TableLinkStyleProvider>
    </div>
  )
}
